import { useState, useEffect, useMemo } from 'react';
import { TH, MP, REWARDS_CATALOG } from '../lib/constants.js';
import { YR, DOY } from '../lib/dateState.js';
import { pushToast, streakDisplayCount, sectionGlowBg } from '../lib/helpers.js';
import { fetchFollowInfo, sendFollowRequest, cancelOrUnfollow, fetchRemoteProfile, fetchRemoteBooks, fetchMyMicroBooks } from '../lib/api.js';
import { Av, Btn, FlameBadge } from '../components/Basic.jsx';
import { NavIcon } from '../components/Navigation.jsx';
import { Bookshelf } from '../components/Shelf.jsx';
import { MicroDetail } from './Micro.jsx';
import { t as tr } from '../lib/i18n.js';
export function ProfileView({username,t,theme,st,dispatch,go,goBack,userId,onNavTheme}){
  const isMe=!username||username==="me";
  const isDemo=false;
  const isReal=!isMe&&!isDemo;
  const myTheme=theme;
  const [remoteProfile,setRemoteProfile]=useState(null);
  const [remoteBooksData,setRemoteBooksData]=useState(null);
  const [remoteMicroBooks,setRemoteMicroBooks]=useState([]);
  const [followInfo,setFollowInfo]=useState({followers:0,following:0,status:null,libraryVisible:false});
  const [myFollowCounts,setMyFollowCounts]=useState({followers:0,following:0});
  const [loadingRemote,setLoadingRemote]=useState(false);
  const [followBusy,setFollowBusy]=useState(false);
  const [viewingMicroBook,setViewingMicroBook]=useState(null);
  useEffect(()=>{
    if(!isMe||!userId) return;
    let mounted=true;
    fetchFollowInfo(userId,userId).then(info=>{ if(mounted) setMyFollowCounts({followers:info.followers,following:info.following}); });
    return ()=>{ mounted=false; };
  },[isMe,userId]);
  useEffect(()=>{
    if(!isReal||!username) return;
    let mounted=true;
    setLoadingRemote(true);
    (async()=>{
      const prof=await fetchRemoteProfile(username);
      if(!mounted) return;
      if(prof){
        setRemoteProfile(prof);
        const [books,follow,microBooks]=await Promise.all([
          fetchRemoteBooks(prof.id),
          fetchFollowInfo(userId,prof.id),
          fetchMyMicroBooks(prof.id),
        ]);
        if(!mounted) return;
        setRemoteBooksData(books);
        setFollowInfo(follow);
        setRemoteMicroBooks(microBooks);
      }
      setLoadingRemote(false);
    })();
    return ()=>{ mounted=false; };
  },[username,isReal,userId]);
  const handleFollow=async()=>{
    if(!userId||!remoteProfile||followBusy) return;
    setFollowBusy(true);
    if(followInfo.status==="accepted"||followInfo.status==="pending_sent"){
      // Smetto di seguire, oppure annullo una richiesta che avevo mandato.
      const ok=await cancelOrUnfollow(userId,remoteProfile.id);
      if(ok){
        setFollowInfo(f=>({...f,status:null,followers:f.status==="accepted"?f.followers-1:f.followers,libraryVisible:false}));
      } else {
        pushToast(dispatch,tr("prof_toast_generic_fail"));
      }
    } else {
      // status è null oppure "pending_received" (loro hanno chiesto di
      // seguire me — un rapporto indipendente, non riguarda questo pulsante)
      const ok=await sendFollowRequest(userId,remoteProfile.id);
      if(ok){
        setFollowInfo(f=>({...f,status:"pending_sent"}));
      } else {
        pushToast(dispatch,tr("prof_toast_generic_fail"));
      }
    }
    setFollowBusy(false);
  };
  const ud=isMe
    ?{...st.profile,theme:myTheme,followers:myFollowCounts.followers,following:myFollowCounts.following,open:false,dailyStreak:st.rewards&&st.rewards.dailyStreak}
    :isReal
      ?{username:(remoteProfile&&remoteProfile.username)||username,bio:(remoteProfile&&remoteProfile.bio)||"",theme:(remoteProfile&&remoteProfile.theme)||"neon",avatarUrl:(remoteProfile&&remoteProfile.avatar_url)||null,coverUrl:(remoteProfile&&remoteProfile.cover_url)||null,followers:followInfo.followers,following:followInfo.following,verified:!!(remoteProfile&&remoteProfile.verified),open:!!(remoteProfile&&remoteProfile.verified)||followInfo.libraryVisible,dailyStreak:remoteProfile&&remoteProfile.rewards&&remoteProfile.rewards.dailyStreak}
      :{theme:"neon",followers:0,following:0,open:false};
  // Badge "serie" accanto al nome — visibile il giorno in cui sono stati
  // completati tutti gli obiettivi giornalieri E il giorno successivo (vedi
  // streakDisplayCount in helpers.js: quel giorno in più è la finestra
  // reale per allungare la serie senza perderla, prima non c'era e il
  // numero spariva ogni mattina finché non si rifacevano gli obiettivi).
  const streakCount=streakDisplayCount(ud.dailyStreak,DOY,YR);
  const streakBadge=streakCount>0?streakCount:null;
  const pt=TH[isMe?myTheme:(ud.theme||"neon")]||TH.neon;
  // La barra in basso (ora un'unica istanza ferma in App.jsx) sul Profilo si
  // tinge SEMPRE del tema di chi si sta guardando, non del proprio — così
  // era anche prima. App.jsx non ha modo di calcolare "pt" da solo (dipende
  // da ud, caricato qui dentro in modo asincrono), quindi glielo passiamo.
  useEffect(()=>{ if(onNavTheme) onNavTheme(pt); },[pt.id]);
  const books=isMe
    ? Object.entries(st.books).map(([y,b])=>({year:parseInt(y),theme:myTheme,filled:b.pages?.length||0}))
    : isReal
      ? Object.entries(remoteBooksData||{}).map(([y,b])=>({year:parseInt(y),theme:ud.theme,filled:b.pages?.length||0}))
      : [{year:YR-1,theme:ud.theme,filled:365},{year:YR,theme:ud.theme,filled:120}];
  // Unified shelf: annual books + (for my own shelf) micro-libri mixed in as
  // visibly smaller volumes — a shelf of different sizes, not identical tiles.
  const shelfItems=useMemo(()=>{
    const annualItems=books.map(b=>{
      const bt=TH[b.theme]||pt;
      return {key:`annual-${b.year}`,kind:"annual",year:b.year,filled:b.filled,max:365,
        spineBg:bt.pill,accent:bt.ac,emoji:bt.emoji,isActive:b.year===YR,leanSeed:((b.year*7)%5)-2};
    });
    if(!isMe){
      const theirMicroItems=(isReal?remoteMicroBooks:[]).map(m=>{
        const preset=MP.find(p=>p.id===m.preset)||MP[0];
        const bt=TH[m.theme]||TH.neon;
        return {key:`micro-${m.id}`,kind:"micro",microId:m.id,title:m.title,filled:m.pages,max:m.max||20,
          spineBg:bt.pill,accent:bt.ac,emoji:m.cover||preset.icon,isActive:false,
          badge:"👁️",
          leanSeed:((String(m.id).length*11+7))%5-2};
      });
      return [...annualItems,...theirMicroItems];
    }
    const microItems=(st.micro||[]).map(m=>{
      const preset=MP.find(p=>p.id===m.preset)||MP[0];
      const bt=TH[m.theme]||TH.neon;
      return {key:`micro-${m.id}`,kind:"micro",microId:m.id,title:m.title,filled:m.pages,max:m.max||20,
        spineBg:bt.pill,accent:bt.ac,emoji:m.cover||preset.icon,isActive:false,
        leanSeed:((String(m.id).length*11+m.id%7))%5-2};
    });
    const sharedItems=(st.sharedBooks||[]).map(sb=>{
      const preset=MP.find(p=>p.id===sb.preset)||MP[0];
      const bt=TH[sb.theme]||TH.neon;
      return {key:`shared-${sb.id}`,kind:"shared",sharedId:sb.id,title:sb.title,filled:sb.pages,max:sb.max||20,
        spineBg:bt.pill,accent:bt.ac,emoji:sb.cover||preset.icon,isActive:false,
        badge:sb.kind==="family"?"👨‍👩‍👧‍👦":"🤝",
        leanSeed:((String(sb.id).length*13+7))%5-2};
    });
    return [...annualItems,...microItems,...sharedItems];
  },[books.map(b=>b.year+"-"+b.filled).join(","),isMe,isReal,st.micro,st.sharedBooks,remoteMicroBooks,pt]);
  // Le decorazioni (coccarde/divisori) vivono in un sistema di posizionamento
  // separato dai libri — libere ovunque sullo scaffale, non incolonnate
  // nella stessa fila ordinata dei libri.
  // Bug corretto il 30/08/2026: per un profilo altrui usava sempre `[]` invece
  // delle vere decorazioni del proprietario — chi seguiva qualcuno vedeva
  // sempre uno scaffale spoglio, mai le coccarde/sticker che quella persona
  // aveva davvero equipaggiato (dato comunque già scaricato in
  // remoteProfile.rewards da fetchRemoteProfile, semplicemente mai letto qui).
  const sourceRewards=isMe?st.rewards:(remoteProfile&&remoteProfile.rewards)||{};
  const equippedDecorations=(sourceRewards.equippedDecorations||[]).map(id=>{
    const r=REWARDS_CATALOG.find(x=>x.id===id);
    return r?{id,emoji:r.emoji,name:r.name}:null;
  }).filter(Boolean);
  // Bug corretto il 30/08/2026 (insieme alle decorazioni sopra): l'ordine
  // dello scaffale ora viaggia dentro rewards.shelfOrder (sincronizzato su
  // Supabase, vedi reducer.js SET_SHELF_ORDER/SET_REWARDS) — per un profilo
  // altrui va letto da lì, mai da st.shelfOrder (che è sempre il MIO ordine
  // locale, prima veniva usato per errore anche guardando profili altrui).
  const shelfOrderSource=isMe?st.shelfOrder:sourceRewards.shelfOrder;
  const orderedShelfKeys=(shelfOrderSource&&shelfOrderSource.length)
    ? [...shelfOrderSource.filter(k=>shelfItems.some(i=>i.key===k)),...shelfItems.filter(i=>!shelfOrderSource.includes(i.key)).map(i=>i.key)]
    : shelfItems.map(i=>i.key);
  const shelfItemsOrdered=orderedShelfKeys.map(k=>shelfItems.find(i=>i.key===k)).filter(Boolean);
  // Micro-libri e libri condivisi da mostrare come schede dettagliate sotto
  // lo scaffale — la stessa idea già usata per i libri annuali, mancava per
  // questi. Per il mio profilo uso st.micro/st.sharedBooks; per un profilo
  // altrui, quelli caricati da remoto (solo micro, i condivisi restano privati).
  const microCards=isMe?(st.micro||[]):(isReal?remoteMicroBooks:[]);
  const sharedCards=isMe?(st.sharedBooks||[]):[];
  {/* viewOnly=true: qui si apre SEMPRE il micro-libro di un ALTRO utente (dal suo
      profilo) — mai il proprio (per il proprio si passa da Micro.jsx, che passa
      userId e lascia viewOnly al default false). Vedi nota su MicroDetail. */}
  if(viewingMicroBook) return <MicroDetail book={viewingMicroBook} go={go} onBack={()=>setViewingMicroBook(null)} dispatch={dispatch} viewOnly/>;
  return(
    <div className="screen fade" style={{background:sectionGlowBg("profile",pt),display:"flex",flexDirection:"column"}}>
      <div style={{flex:1,overflowY:"auto"}}>
      {/* Hero rifatto il 31/08/2026 (richiesta di Matte): copertina e avatar
          scorporati dal resto in una card unica "in rilievo" (margini,
          angoli arrotondati, ombra), con la copertina come UNICO sfondo
          continuo che scorre da sopra a sotto — niente più due blocchi di
          colore separati. L'avatar sta più in alto, libero dal testo, con
          un anello sottile e un'ombra propria (non più il bordo bianco
          spesso) per leggersi come sospeso sopra la copertina. Fiamma,
          spunta verificata, bio, statistiche, follow e coccarde sono
          esattamente gli stessi componenti/valori di prima — solo spostati
          qui dentro, nessuna logica toccata. */}
      <div style={{margin:"14px 14px 8px",borderRadius:22,overflow:"hidden",position:"relative",boxShadow:"0 16px 30px rgba(0,0,0,.35)",flexShrink:0}}>
        {/* Fix 31/08/2026 (segnalato da Matte: "taglia una parte della foto
            che prima era in vista"): la copertina non copre più tutta
            l'altezza variabile della card (che con bio/coccarde poteva
            arrivare a 300+ px, ritagliando la foto molto più di prima) —
            resta in una fascia alta fissa, vicina a quella di prima, e la
            sfumatura sotto continua col colore del tema in modo che non si
            veda comunque nessuna cucitura. */}
        <div style={{position:"absolute",top:0,left:0,right:0,height:172,overflow:"hidden",zIndex:0}}>
          {ud.coverUrl
            ? <img src={ud.coverUrl} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            : <div style={{width:"100%",height:"100%",background:pt.pill}}/>}
          {!ud.coverUrl&&<div style={{position:"absolute",inset:0,background:pt.pat,pointerEvents:"none"}}/>}
          <div style={{position:"absolute",inset:0,background:"radial-gradient(ellipse at top,rgba(255,255,255,.16),transparent 60%)",pointerEvents:"none"}}/>
        </div>
        <div style={{position:"absolute",inset:0,background:`linear-gradient(180deg,transparent 0px,transparent 56px,${pt.bg}CC 116px,${pt.bg}F8 172px)`,pointerEvents:"none",zIndex:1}}/>
        {/* Fix 02/09/2026 (Matte: "non mi fa cliccare sul pulsante
            impostazioni"): questo pulsante e il blocco sotto (nome/bio/
            statistiche) avevano lo STESSO zIndex:2 — a parità di zIndex
            vince chi viene dopo nel markup, e il blocco sotto viene dopo.
            La sua area di padding-top:80 in alto è vuota alla vista ma
            resta comunque "cliccabile" (nessun background non vuol dire
            non-interattiva), quindi si prendeva lei il tocco anche sopra
            il cerchio dell'icona. I tre pulsanti in alto ora stanno un
            livello sopra (zIndex:3), così vincono sempre loro. */}
        {!isMe&&<div onClick={()=>goBack?goBack():go("feed")} style={{position:"absolute",top:15,left:15,width:32,height:32,borderRadius:"50%",background:"rgba(0,0,0,.25)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:14,color:"#fff",zIndex:3}}>←</div>}
        {!isMe&&<div onClick={()=>go("notebook",{user:username})} style={{position:"absolute",top:15,right:15,width:32,height:32,borderRadius:"50%",background:"rgba(0,0,0,.25)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",zIndex:3}}><NavIcon type="chat" active={false} soft="#fff" size={17}/></div>}
        {isMe&&<div onClick={()=>go("settings")} style={{position:"absolute",top:15,right:15,width:32,height:32,borderRadius:"50%",background:"rgba(0,0,0,.25)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",zIndex:3}}><NavIcon type="settings" active={false} soft="#fff" size={17}/></div>}
        <div style={{position:"relative",zIndex:2,paddingTop:80}}>
          <div style={{marginLeft:16,marginTop:-42,width:70,height:70,borderRadius:"50%",border:"1.5px solid rgba(255,255,255,.45)",boxShadow:"0 12px 22px rgba(0,0,0,.5),inset 0 2px 3px rgba(255,255,255,.35),inset 0 -2px 4px rgba(0,0,0,.2)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <Av u={isMe?(st.profile.username||"tu"):username} tid={isMe?myTheme:ud.theme} sz={64} photo={ud.avatarUrl}/>
          </div>
          <div style={{padding:"9px 16px 16px"}}>
            <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
              <span style={{fontSize:18,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif"}}>@{isMe?(st.profile.username||"tu"):username}</span>
              {ud.verified&&(
                <svg width="14" height="19" viewBox="0 0 26 36" fill="none" style={{flexShrink:0}}>
                  <defs>
                    <linearGradient id="oncerVerGradProfile" x1="2" y1="2" x2="24" y2="34" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stopColor="#FF2D6B"/>
                      <stop offset="1" stopColor="#7B2FFF"/>
                    </linearGradient>
                  </defs>
                  <path d="M5 2 L21 2 A3 3 0 0 1 24 5 L24 34 L13 26 L2 34 L2 5 A3 3 0 0 1 5 2 Z" fill="url(#oncerVerGradProfile)"/>
                  <path d="M8 16 L11.5 19.5 L18 12" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              )}
              <FlameBadge count={streakBadge||0} sz={24} title={streakBadge?tr("prof_streak_badge_title",{n:streakBadge}):tr("prof_streak_badge_off_title")}/>
            </div>
            {ud.bio
              ? <div style={{fontSize:12,color:"rgba(255,255,255,.78)",fontFamily:"Inter,sans-serif",lineHeight:1.45,marginTop:5}}>{ud.bio}</div>
              : isMe&&<div onClick={()=>go("settings")} style={{fontSize:11.5,color:"rgba(255,255,255,.5)",fontFamily:"Inter,sans-serif",fontStyle:"italic",marginTop:5,cursor:"pointer"}}>{tr("prof_add_bio")}</div>}
            {isReal&&loadingRemote&&<div style={{fontSize:11,color:"rgba(255,255,255,.5)",fontFamily:"Inter,sans-serif",marginTop:5}}>{tr("prof_loading_profile")}</div>}
            {ud.open&&<span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"2px 9px",borderRadius:20,background:"#FFD70022",fontSize:9,fontFamily:"Inter,sans-serif",fontWeight:700,color:"#FFD700",marginTop:8}}>{tr("prof_open_library_badge")}</span>}
            <div style={{display:"flex",gap:18,marginTop:10}}>
              {[{v:ud.followers.toLocaleString(),l:tr("prof_stat_followers")},{v:ud.following,l:tr("prof_stat_following")},{v:books.length,l:tr("prof_stat_books")}].map(({v,l})=>(
                <div key={l}><div style={{fontSize:16,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif"}}>{v}</div><div style={{fontSize:9,color:"rgba(255,255,255,.6)",fontFamily:"Inter,sans-serif"}}>{l}</div></div>
              ))}
            </div>
            {isReal&&userId&&!loadingRemote&&remoteProfile&&(
              <Btn fn={handleFollow} style={{marginTop:10,padding:"9px 26px",borderRadius:40,fontSize:12.5,fontWeight:700,fontFamily:"Inter,sans-serif",background:followInfo.status?"rgba(255,255,255,.15)":"#fff",color:followInfo.status?"#fff":pt.ac,border:followInfo.status?"1px solid rgba(255,255,255,.3)":"none",opacity:followBusy?.6:1,display:"inline-block"}}>
                {followInfo.status==="accepted"?tr("prof_following_already"):followInfo.status==="pending_sent"?tr("prof_request_sent"):tr("disc_follow_btn")}
              </Btn>
            )}
            {isMe&&st.rewards.unlocked.some(id=>{const r=REWARDS_CATALOG.find(x=>x.id===id); return r&&(r.type==="badge"||r.type==="divider");})&&(
              <div style={{display:"flex",gap:6,marginTop:10,flexWrap:"wrap",maxWidth:280}}>
                {st.rewards.unlocked.map(id=>REWARDS_CATALOG.find(r=>r.id===id)).filter(r=>r&&(r.type==="badge"||r.type==="divider")).map(r=>(
                  <span key={r.id} title={r.name} style={{fontSize:18,background:(st.rewards.equippedDecorations||[]).includes(r.id)?"rgba(255,215,0,.28)":"rgba(0,0,0,.25)",border:(st.rewards.equippedDecorations||[]).includes(r.id)?"1.5px solid #FFD700":"1.5px solid transparent",borderRadius:"50%",width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center"}}>{r.emoji}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {isMe&&(
        // Vetrificati il 31/08/2026: gradiente di marca ancora presente ma
        // reso translucido (alpha in coda ai colori) + blur/saturate/bordo/
        // riflesso, stesso linguaggio del resto — non più un blocco di
        // colore pieno e opaco.
        <div style={{display:"flex",gap:10,margin:"0 13px 16px"}}>
          <div onClick={()=>go("goals")} style={{width:74,height:74,flexShrink:0,background:"linear-gradient(135deg,#FF2D6Bcc,#7B2FFFcc)",backdropFilter:"blur(18px) saturate(190%)",WebkitBackdropFilter:"blur(18px) saturate(190%)",border:"1px solid rgba(255,255,255,.28)",borderRadius:16,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:3,boxShadow:"0 12px 24px rgba(255,45,107,.32),0 3px 8px rgba(0,0,0,.22),inset 0 1.5px 0 rgba(255,255,255,.22),inset 0 -1px 0 rgba(0,0,0,.18)",padding:6,textAlign:"center"}}>
            <div style={{fontSize:19}}>🎯</div>
            <div style={{fontSize:9,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif",lineHeight:1.15}}>{tr("prof_goals_label")}</div>
          </div>
          <div onClick={()=>go("oncer_future")} style={{width:74,height:74,flexShrink:0,background:"linear-gradient(135deg,#1a1030cc,#3a2060cc)",backdropFilter:"blur(18px) saturate(190%)",WebkitBackdropFilter:"blur(18px) saturate(190%)",border:"1px solid rgba(255,255,255,.2)",borderRadius:16,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:3,boxShadow:"0 12px 24px rgba(58,32,96,.4),0 3px 8px rgba(0,0,0,.24),inset 0 1.5px 0 rgba(255,255,255,.18),inset 0 -1px 0 rgba(0,0,0,.2)",padding:6,textAlign:"center"}}>
            <div style={{fontSize:19}}>⏳</div>
            <div style={{fontSize:9,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif",lineHeight:1.15}}>Oncer Future</div>
          </div>
        </div>
      )}
      {/* Books */}
      {/* padding-bottom aumentato il 31/08/2026 (richiesta di Matte): con la
          barra delle sezioni sempre fissa in basso, l'ultimo micro-libro
          finiva nascosto/premuto sotto di lei — ora c'è spazio vero per
          respirare oltre l'ultima card. */}
      <div style={{padding:"0 13px 110px"}}>
        <div style={{fontSize:10,fontWeight:700,color:pt.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1.5px",textTransform:"uppercase",marginBottom:10}}>{tr("prof_annual_books_label")}</div>
        {(!isMe&&!ud.open)?(
          /* Private library — locked placeholder, no detail leaked */
          <div style={{background:pt.card,borderRadius:14,border:`1px solid ${pt.bord}`,padding:"28px 20px",textAlign:"center"}}>
            <div style={{fontSize:34,marginBottom:10}}>🔒</div>
            <div style={{fontSize:14,fontWeight:700,color:pt.tx,fontFamily:"Inter,sans-serif",marginBottom:6}}>{tr("prof_private_library_title")}</div>
            <div style={{fontSize:12,color:pt.soft,fontFamily:"Inter,sans-serif",lineHeight:1.6}}>{tr("prof_private_desc1",{username})}<br/>{tr("prof_private_desc2")}</div>
          </div>
        ):(
          <>
            <div>
              <Bookshelf items={shelfItemsOrdered} dividers={sourceRewards.dividers} editable={isMe} layoutEditMode={false} onReorder={(order)=>dispatch({type:"SET_SHELF_ORDER",order})}
                decorations={equippedDecorations}
                decorationPositions={sourceRewards.decorationPositions||{}}
                onDecorationMove={(id,x,y)=>{
                  if(!isMe) return; // sola visualizzazione sul profilo altrui — vedi sourceRewards sopra
                  const nextPositions={...(st.rewards.decorationPositions||{}),[id]:{x,y}};
                  dispatch({type:"SET_REWARDS",rewards:{...st.rewards,decorationPositions:nextPositions}});
                }}
                onItemClick={(item)=>{
                  if(item.kind==="annual") go(isMe?"library":"library_"+username);
                  else if(!isMe&&item.kind==="micro"){
                    const found=remoteMicroBooks.find(m=>String(m.id)===String(item.microId));
                    if(found) setViewingMicroBook(found);
                  }
                  else if(!isMe) go("library_"+username);
                  else if(item.kind==="shared") go("micro_open_shared_"+item.sharedId);
                  else if(item.kind==="micro") go("micro_open_"+item.microId);
                }}/>
            </div>
            {books.slice().reverse().map(b=>{
              const bt=TH[b.theme]||pt;const ia=b.year===YR;
              const clickable=ia;
              return(
                <div key={b.year} onClick={()=>clickable&&go(isMe?"library":"library_"+username)} style={{background:pt.bg+"78",borderRadius:16,marginBottom:9,border:ia?`1.5px solid ${pt.ac}`:`1px solid ${pt.ac}40`,boxShadow:"0 8px 18px rgba(0,0,0,.26),inset 0 1.5px 0 rgba(255,255,255,.13)",overflow:"hidden",display:"flex",cursor:clickable?"pointer":"default"}}>
                  <div style={{width:5,flexShrink:0,background:bt.pill}}/>
                  <div style={{padding:"12px 13px",flex:1,display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:33,height:41,borderRadius:4,background:bt.pill,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{bt.emoji}</div>
                    <div style={{flex:1}}><div style={{fontSize:16,fontWeight:700,color:pt.tx,fontFamily:"Inter,sans-serif"}}>{b.year}</div><div style={{fontSize:10,color:pt.soft,fontFamily:"Inter,sans-serif",marginTop:1}}>{tr("prof_book_meta",{theme:bt.name,pages:b.filled})}</div>{ia&&<div style={{marginTop:5,height:3,background:pt.soft+"22",borderRadius:2,overflow:"hidden"}}><div style={{height:"100%",width:`${(b.filled/365)*100}%`,background:bt.pill,borderRadius:2}}/></div>}</div>
                    {ia&&<div style={{fontSize:8,padding:"3px 7px",borderRadius:20,background:pt.ac,color:"#fff",fontFamily:"Inter,sans-serif",fontWeight:700}}>{tr("prof_in_progress_badge")}</div>}
                    {clickable&&<div style={{fontSize:14,color:pt.soft}}>›</div>}
                  </div>
                </div>
              );
            })}
            {microCards.length>0&&(
              <>
                <div style={{fontSize:10,fontWeight:700,color:pt.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1.5px",textTransform:"uppercase",margin:"18px 0 10px"}}>{tr("prof_micro_books_label")}</div>
                {microCards.map(m=>{
                  const preset=MP.find(p=>p.id===m.preset)||MP[0];
                  const bt=TH[m.theme]||TH.neon;
                  return(
                    <div key={m.id} onClick={()=>{ if(isMe) go("micro"); else setViewingMicroBook(m); }} style={{background:pt.bg+"78",borderRadius:16,marginBottom:9,border:`1px solid ${pt.ac}40`,boxShadow:"0 8px 18px rgba(0,0,0,.26),inset 0 1.5px 0 rgba(255,255,255,.13)",overflow:"hidden",display:"flex",cursor:"pointer"}}>
                      <div style={{width:5,flexShrink:0,background:bt.pill}}/>
                      <div style={{padding:"12px 13px",flex:1,display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:33,height:41,borderRadius:4,background:bt.pill,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{m.cover||preset.icon}</div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:16,fontWeight:700,color:pt.tx,fontFamily:"Inter,sans-serif"}}>{m.title}</div>
                          <div style={{fontSize:10,color:pt.soft,fontFamily:"Inter,sans-serif",marginTop:1}}>{tr("prof_micro_meta",{pages:m.pages})}</div>
                          <div style={{marginTop:5,height:3,background:pt.soft+"22",borderRadius:2,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.min(100,(m.pages/(m.max||20))*100)}%`,background:bt.pill,borderRadius:2}}/></div>
                        </div>
                        {m.closed&&<div style={{fontSize:8,padding:"3px 7px",borderRadius:20,background:"#FFD70022",color:"#FFD700",fontFamily:"Inter,sans-serif",fontWeight:700}}>🔒</div>}
                        <div style={{fontSize:14,color:pt.soft}}>›</div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
            {sharedCards.length>0&&(
              <>
                <div style={{fontSize:10,fontWeight:700,color:pt.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1.5px",textTransform:"uppercase",margin:"18px 0 10px"}}>{tr("prof_shared_books_label")}</div>
                {sharedCards.map(sb=>{
                  const preset=MP.find(p=>p.id===sb.preset)||MP[0];
                  const bt=TH[sb.theme]||TH.neon;
                  return(
                    <div key={sb.id} onClick={()=>go("micro")} style={{background:pt.bg+"78",borderRadius:16,marginBottom:9,border:`1px solid ${pt.ac}40`,boxShadow:"0 8px 18px rgba(0,0,0,.26),inset 0 1.5px 0 rgba(255,255,255,.13)",overflow:"hidden",display:"flex",cursor:"pointer"}}>
                      <div style={{width:5,flexShrink:0,background:bt.pill}}/>
                      <div style={{padding:"12px 13px",flex:1,display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:33,height:41,borderRadius:4,background:bt.pill,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{sb.cover||preset.icon}</div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:16,fontWeight:700,color:pt.tx,fontFamily:"Inter,sans-serif"}}>{sb.title} {sb.kind==="family"?"👨‍👩‍👧‍👦":"🤝"}</div>
                          <div style={{fontSize:10,color:pt.soft,fontFamily:"Inter,sans-serif",marginTop:1}}>{tr("prof_pages_count",{n:sb.pages})}</div>
                        </div>
                        <div style={{fontSize:14,color:pt.soft}}>›</div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>
      </div>
      {/* BNav: dal 31/08/2026 un'unica istanza ferma in App.jsx, tinta col
          tema "pt" segnalato qui sopra tramite onNavTheme. */}
    </div>
  );
}
